import React from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';

const AnnouncementPage = () => {
  const navigate = useNavigate();

  const newFeatures = [
    {
      icon: 'auto_awesome',
      title: 'Enhanced AI Engine',
      description: 'Our new neural core processes complex workflows 3x faster with improved accuracy and decision-making capabilities.',
      status: 'New'
    },
    {
      icon: 'hub',
      title: 'Advanced Integration Hub',
      description: 'Connect to 500+ new enterprise applications with our expanded connector library and real-time sync capabilities.',
      status: 'Enhanced'
    },
    {
      icon: 'security',
      title: 'Zero-Trust Security',
      description: 'Military-grade encryption with advanced threat detection and automated security response protocols.',
      status: 'New'
    },
    {
      icon: 'analytics',
      title: 'Predictive Analytics',
      description: 'AI-powered insights that predict workflow bottlenecks and suggest optimizations before issues occur.',
      status: 'Enhanced'
    },
    {
      icon: 'speed',
      title: 'Lightning Performance',
      description: 'Sub-50ms response times with our new distributed architecture and edge computing capabilities.',
      status: 'Improved'
    },
    {
      icon: 'tune',
      title: 'No-Code Workflow Builder',
      description: 'Drag-and-drop interface with AI-assisted workflow creation and automatic optimization suggestions.',
      status: 'New'
    }
  ];

  const improvements = [
    {
      category: 'Performance',
      items: [
        '3x faster workflow execution',
        '50% reduction in response time',
        '99.99% uptime guarantee',
        'Auto-scaling infrastructure'
      ]
    },
    {
      category: 'Security',
      items: [
        'End-to-end encryption',
        'Advanced threat detection',
        'Compliance with SOC 2 Type II',
        'Multi-factor authentication'
      ]
    },
    {
      category: 'Integration',
      items: [
        '500+ new app connectors',
        'Real-time data synchronization',
        'API rate limiting protection',
        'Webhook reliability improvements'
      ]
    },
    {
      category: 'User Experience',
      items: [
        'Redesigned dashboard interface',
        'Mobile-responsive design',
        'Dark/light theme support',
        'Accessibility improvements'
      ]
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'New': return 'text-emerald-500 bg-emerald-500/10';
      case 'Enhanced': return 'text-blue-500 bg-blue-500/10';
      case 'Improved': return 'text-purple-500 bg-purple-500/10';
      default: return 'text-slate-500 bg-slate-500/10';
    }
  };

  return (
    <div className="min-h-screen bg-dark">
      <Navbar />
      
      <main className="px-6 md:px-20 lg:px-40 py-12">
        <div className="max-w-6xl mx-auto">
          
          {/* Header Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2 mb-6">
              <span className="material-symbols-outlined text-primary text-sm">campaign</span>
              <span className="text-primary text-sm font-bold uppercase tracking-wider">Special Announcement</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-black text-white mb-6 tracking-tight">
              OrbitMind 2.0 is Here
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-400 mb-8 max-w-3xl mx-auto leading-relaxed">
              Introducing the most powerful Enterprise Workflow Engine ever built. 
              Experience unprecedented automation capabilities with our revolutionary AI-driven platform.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => navigate('/signup')}
                className="bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors flex items-center justify-center gap-2"
              >
                <span className="material-symbols-outlined">rocket_launch</span>
                Start Free Trial
              </button>
              <button 
                onClick={() => navigate('/dashboard')}
                className="bg-white/10 hover:bg-white/20 text-white border border-white/20 px-8 py-4 rounded-lg font-bold text-lg transition-colors flex items-center justify-center gap-2"
              >
                <span className="material-symbols-outlined">play_arrow</span>
                View Demo
              </button>
            </div>
          </div>

          {/* Release Highlights */}
          <div className="mb-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-black text-white mb-4">What's New in 2.0</h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                Discover the groundbreaking features that make OrbitMind 2.0 the ultimate automation platform
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {newFeatures.map((feature, index) => (
                <div key={index} className="bg-white/5 border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-colors">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <span className="material-symbols-outlined text-primary text-xl">{feature.icon}</span>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${getStatusColor(feature.status)}`}>
                      {feature.status}
                    </span>
                  </div>
                  <h3 className="text-white font-bold text-xl mb-3">{feature.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Improvements Grid */}
          <div className="mb-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-black text-white mb-4">Key Improvements</h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                Every aspect of OrbitMind has been enhanced for better performance, security, and user experience
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {improvements.map((category, index) => (
                <div key={index} className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <h3 className="text-white font-bold text-xl mb-4 flex items-center gap-2">
                    <span className="material-symbols-outlined text-primary">
                      {category.category === 'Performance' ? 'speed' : 
                       category.category === 'Security' ? 'security' :
                       category.category === 'Integration' ? 'hub' : 'design_services'}
                    </span>
                    {category.category}
                  </h3>
                  <ul className="space-y-3">
                    {category.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-center gap-3 text-gray-300">
                        <span className="material-symbols-outlined text-emerald-500 text-sm">check_circle</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Migration & Upgrade Info */}
          <div className="bg-gradient-to-br from-primary/10 to-blue-500/10 border border-primary/20 rounded-xl p-8 mb-16">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-black text-white mb-4">Seamless Migration</h2>
              <p className="text-gray-300 text-lg max-w-2xl mx-auto">
                Upgrading to OrbitMind 2.0 is automatic for all existing users. No downtime, no data loss, no hassle.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="material-symbols-outlined text-primary text-2xl">backup</span>
                </div>
                <h3 className="text-white font-bold text-lg mb-2">Automatic Backup</h3>
                <p className="text-gray-400 text-sm">All your workflows and data are automatically backed up before migration</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="material-symbols-outlined text-primary text-2xl">sync</span>
                </div>
                <h3 className="text-white font-bold text-lg mb-2">Zero Downtime</h3>
                <p className="text-gray-400 text-sm">Migration happens in the background with no service interruption</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="material-symbols-outlined text-primary text-2xl">verified</span>
                </div>
                <h3 className="text-white font-bold text-lg mb-2">Compatibility</h3>
                <p className="text-gray-400 text-sm">All existing workflows continue to work with enhanced performance</p>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center bg-white/5 border border-white/10 rounded-xl p-12">
            <h2 className="text-3xl font-black text-white mb-4">Ready to Experience OrbitMind 2.0?</h2>
            <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
              Join thousands of enterprises already leveraging the power of our next-generation automation platform.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => navigate('/signup')}
                className="bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors flex items-center justify-center gap-2"
              >
                <span className="material-symbols-outlined">person_add</span>
                Get Started Free
              </button>
              <button 
                onClick={() => navigate('/contact')}
                className="bg-white/10 hover:bg-white/20 text-white border border-white/20 px-8 py-4 rounded-lg font-bold text-lg transition-colors flex items-center justify-center gap-2"
              >
                <span className="material-symbols-outlined">support_agent</span>
                Contact Sales
              </button>
            </div>
          </div>

          {/* Back to Home */}
          <div className="text-center mt-12">
            <button 
              onClick={() => navigate('/')}
              className="text-primary hover:text-primary/80 font-semibold flex items-center justify-center gap-2 mx-auto transition-colors"
            >
              <span className="material-symbols-outlined">arrow_back</span>
              Back to Home
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AnnouncementPage;